import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

type TitleContextValue = {
  title: string;
  setOverride: (next: string | null) => void;
  routeTitle: string;
  setRouteTitle: (next: string) => void;
};

const TitleContext = createContext<TitleContextValue | null>(null);

export const TitleProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [routeTitle, setRouteTitle] = useState("");
  const [override, setOverride] = useState<string | null>(null);

  const value = useMemo<TitleContextValue>(
    () => ({
      title: override ?? routeTitle,
      setOverride,
      routeTitle,
      setRouteTitle,
    }),
    [override, routeTitle],
  );

  return <TitleContext.Provider value={value}>{children}</TitleContext.Provider>;
};

const useTitleContext = () => {
  const ctx = useContext(TitleContext);
  if (!ctx) {
    throw new Error("useTitle must be used inside TitleProvider");
  }
  return ctx;
};

export const useTitleValue = () => useTitleContext().title;

export const useSetRouteTitle = (t: string) => {
  const { setRouteTitle } = useTitleContext();
  useEffect(() => {
    setRouteTitle(t);
  }, [t, setRouteTitle]);
};

export const useTitle = (t: string) => {
  const { setOverride } = useTitleContext();
  useEffect(() => {
    if (!t) return;
    setOverride(t);
    return () => setOverride(null);
  }, [t, setOverride]);
};
